'use client';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useState, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Header } from '@/components/layout';
import { Button, Card, CardHeader, Input } from '@/components/ui';
import { api } from '@/lib/api';

const requestSchema = z.object({
    email: z.string().email(),
});

const resetSchema = z.object({
    new_password: z
        .string()
        .min(8)
        .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .regex(/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/, 'Password must contain at least one special character'),
    confirm_password: z.string(),
}).refine((data) => data.new_password === data.confirm_password, {
    message: "Passwords don't match",
    path: ['confirm_password'],
});

type RequestFormData = z.infer<typeof requestSchema>;
type ResetFormData = z.infer<typeof resetSchema>;

function ResetPasswordContent() {
    const t = useTranslations('auth.resetPassword');
    const tValidation = useTranslations('validation');
    const searchParams = useSearchParams();
    const token = searchParams.get('token');

    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const requestForm = useForm<RequestFormData>({
        resolver: zodResolver(requestSchema),
    });

    const resetForm = useForm<ResetFormData>({
        resolver: zodResolver(resetSchema),
    });

    const onRequestSubmit = async (data: RequestFormData) => {
        setIsLoading(true);
        setError(null);

        try {
            await api.requestPasswordReset(data.email);
            setSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Request failed');
        } finally {
            setIsLoading(false);
        }
    };

    const onResetSubmit = async (data: ResetFormData) => {
        if (!token) return;

        setIsLoading(true);
        setError(null);

        try {
            await api.resetPassword(token, data.new_password);
            setSuccess(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Reset failed');
        } finally {
            setIsLoading(false);
        }
    };

    // Show success message
    if (success) {
        return (
            <Card className="w-full max-w-md text-center">
                <div className="mb-6">
                    <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                </div>
                <h2 className="text-2xl font-bold text-slate-800 mb-4">
                    {token ? t('success') : t('emailSentTitle')}
                </h2>
                <p className="text-slate-600 mb-6">
                    {token ? t('success') : t('emailSent')}
                </p>
                <Link href="/login">
                    <Button>{t('goToLogin')}</Button>
                </Link>
            </Card>
        );
    }

    // Reset password form (when token is present)
    if (token) {
        return (
            <Card className="w-full max-w-md">
                <CardHeader title={t('confirmTitle')} subtitle={t('confirmSubtitle')} />

                {error && (
                    <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-600 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={resetForm.handleSubmit(onResetSubmit)} className="space-y-5">
                    <Input
                        label={t('newPassword')}
                        type="password"
                        autoComplete="new-password"
                        {...resetForm.register('new_password')}
                        error={resetForm.formState.errors.new_password ? tValidation('passwordMin') : undefined}
                    />

                    <Input
                        label={t('confirmPassword')}
                        type="password"
                        autoComplete="new-password"
                        {...resetForm.register('confirm_password')}
                        error={resetForm.formState.errors.confirm_password ? tValidation('passwordMatch') : undefined}
                    />

                    <Button type="submit" className="w-full" isLoading={isLoading}>
                        {t('submitConfirm')}
                    </Button>
                </form>
            </Card>
        );
    }

    // Request password reset form
    return (
        <Card className="w-full max-w-md">
            <CardHeader title={t('requestTitle')} subtitle={t('requestSubtitle')} />

            {error && (
                <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-600 text-sm">
                    {error}
                </div>
            )}

            <form onSubmit={requestForm.handleSubmit(onRequestSubmit)} className="space-y-5">
                <Input
                    label={t('email')}
                    type="email"
                    autoComplete="email"
                    {...requestForm.register('email')}
                    error={requestForm.formState.errors.email ? tValidation('email') : undefined}
                />

                <Button type="submit" className="w-full" isLoading={isLoading}>
                    {t('submit')}
                </Button>
            </form>

            <div className="mt-6 text-center">
                <Link
                    href="/login"
                    className="text-sm text-black hover:underline"
                >
                    {t('backToLogin')}
                </Link>
            </div>
        </Card>
    );
}

export default function ResetPasswordPage() {
    return (
        <>

            <main className="min-h-screen flex items-center justify-center px-4 pt-24 pb-12">
                <Suspense fallback={<div>Loading...</div>}>
                    <ResetPasswordContent />
                </Suspense>
            </main>
        </>
    );
}
